import { useGetAllJobPostsQuery } from '@/app/features/recruitment/job-applicants/job-applicants.api';
import { DISCLOSER_KEYS, FILTER_KEYS } from '@/constants/keys.constants';
import { useAppDiscloser, useAppFilters } from '@/hooks';
import useSelectionOptions from '@/hooks/useSelectionOptions';
import { Calendar03Icon, ChevronDownIcon, SearchIcon } from '@/icons';
import { Button, DatePicker, Flex, Input, Select } from 'antd';

const { RangePicker } = DatePicker;

type InterviewSegmentType = 'interview-scheduled' | 'interview-score';

type Props = { type: InterviewSegmentType };

const ScheduledInterviewFilters = () => {
  const { handleFilter, handleSearch } = useAppFilters(
    FILTER_KEYS.INTERVIEW_SCHEDULED,
  );
  const [, handleOpen] = useAppDiscloser(DISCLOSER_KEYS.SCHEDULED_INTERVIEW);

  const [jobOptions, isJobsFetching] = useSelectionOptions(
    useGetAllJobPostsQuery,
    { labelKey: 'job_title', valueKey: 'id' },
  );

  return (
    <div className="flex flex-col items-end gap-4">
      <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
        <Input
          allowClear
          size="large"
          placeholder="Search Applicant"
          prefix={<SearchIcon size={20} />}
          onChange={handleSearch}
        />
        <div>
          <RangePicker
            allowClear
            size="large"
            className="w-[250px]"
            prefix={<Calendar03Icon size={20} />}
            suffixIcon={null}
            onChange={(values) => {
              const [startDate, endDate] = values || [];
              if (startDate && endDate) {
                handleFilter('start_date', startDate.format('YYYY-MM-DD'));
                handleFilter('end_date', endDate.format('YYYY-MM-DD'));
              } else {
                handleFilter('start_date', undefined);
                handleFilter('end_date', undefined);
              }
            }}
          />
        </div>

        <div>
          <Select
            allowClear
            size="large"
            placement="bottomRight"
            placeholder="Job Position"
            popupMatchSelectWidth={false}
            loading={isJobsFetching as boolean}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            onChange={(value) => handleFilter('job_id', value)}
            options={jobOptions as any[]}
            showSearch
            optionFilterProp="label"
          />
        </div>
        <div>
          <Select
            allowClear
            size="large"
            placement="bottomRight"
            placeholder="Status"
            popupMatchSelectWidth={false}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            onChange={(value) => handleFilter('status', value)}
            options={[
              { label: 'In Progress', value: 'in_progress' },
              { label: 'No-Show', value: 'no_show' },
              { label: 'Pending', value: 'pending' },
              { label: 'Passed', value: 'passed' },
              { label: 'Failed', value: 'failed' },
            ]}
          />
        </div>
      </Flex>
      <Button
        size="large"
        className="btn-primary !text-theme-gray-650 !font-bold hover:!text-theme-gray-650 !rounded-md"
        onClick={() => handleOpen({ type: 'ADD' })}
      >
        Schedule New Interview
      </Button>
    </div>
  );
};

const InterviewsScoresFilters = () => {
  const { handleFilter, handleSearch } = useAppFilters(
    FILTER_KEYS.INTERVIEW_SCORES,
  );
  const [, handleOpen] = useAppDiscloser(DISCLOSER_KEYS.INTERVIEW_SCORES);

  const [jobOptions, isJobsFetching] = useSelectionOptions(
    useGetAllJobPostsQuery,
    { labelKey: 'job_title', valueKey: 'id' },
  );

  return (
    <div className="flex flex-col items-end gap-4">
      <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
        <Input
          allowClear
          size="large"
          placeholder="Search Applicant"
          prefix={<SearchIcon size={20} />}
          onChange={handleSearch}
        />
        <div>
          <RangePicker
            allowClear
            size="large"
            className="w-[250px]"
            prefix={<Calendar03Icon size={20} />}
            suffixIcon={null}
            onChange={(values) => {
              const [startDate, endDate] = values || [];
              if (startDate && endDate) {
                handleFilter('start_date', startDate.format('YYYY-MM-DD'));
                handleFilter('end_date', endDate.format('YYYY-MM-DD'));
              } else {
                handleFilter('start_date', undefined);
                handleFilter('end_date', undefined);
              }
            }}
          />
        </div>

        <div>
          <Select
            allowClear
            size="large"
            placement="bottomRight"
            placeholder="Job Position"
            popupMatchSelectWidth={false}
            loading={isJobsFetching as boolean}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            onChange={(value) => handleFilter('job_id', value)}
            options={jobOptions as any[]}
            showSearch
            optionFilterProp="label"
          />
        </div>
        <div>
          <Select
            allowClear
            size="large"
            placement="bottomRight"
            placeholder="Status"
            popupMatchSelectWidth={false}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            onChange={(value) => handleFilter('status', value)}
            options={[
              { value: 'in_progress', label: 'In Progress' },
              { value: 'scheduled', label: 'Scheduled' },
              { value: 'completed', label: 'Completed' },
              { value: 'no_show', label: 'No-Show' },
              { value: 'passed', label: 'Passed' },
              { value: 'failed', label: 'Failed' },
            ]}
          />
        </div>
      </Flex>
      <Button
        size="large"
        className="btn-primary !text-theme-gray-650 !font-bold hover:!text-theme-gray-650 !rounded-md"
        onClick={() => handleOpen({ type: 'ADD' })}
      >
        Schedule New Interview
      </Button>
    </div>
  );
};

const Filters = ({ type }: Props) => {
  switch (type) {
    case 'interview-scheduled':
      return <ScheduledInterviewFilters />;
    case 'interview-score':
      return <InterviewsScoresFilters />;
    default:
      return null;
  }
};
export default Filters;
